/* eslint-disable no-shadow */
import React, { Component } from "react";
import ErrorBoundary from "../../shared/error/ErrorBoundary";
import {
  PerformanceCard,
  Section,
  AgencyCard,
  AgencyDetails,
  AgencyBottomSection,
  Sector,
  CandidateInfo,
  JobsInfo,
  FeesInfo,
  Title,
  AgencyRightSection,
  AgencyImageSection,
  SectorSection,
  CandidateInfoSection,
  JobInfoSection,
  FeesInfoSection,
  LineBreak,
  AgencyNameLink,
} from "../styles/psl-performance-styles";
import moment from "moment";
import { Rating } from "./Components/Rating";
import AgencyImage from "./Components/AgencyImage";
import ReactTooltip from "react-tooltip";

type SubmissionMetric = [string, number];
type JobMetric = [string, number | string];
type FeeMetric = [string, number];

interface PrimaryUser {
  userId: number;
  title: string;
  name: string;
  yearsExperience: number | null;
  activeOnPlatformSince: string; // Date type converted to string for consistency
  activeEngagementCount: number;
  feedbackRating: number;
  profilePicture: string;
  hasSectors: boolean;
}

interface Sector {
  sector_id: number;
  name: string;
  count: number;
}

interface Attributes {
  id: string;
  agencyName: string;
  hhScore: number;
  primaryUser: PrimaryUser;
  sectors: Array<Sector>;
  submissions: Array<SubmissionMetric>;
  jobs: Array<JobMetric>;
  fees: Array<FeeMetric>;
}

export interface IPSLCardProps {
  row: {
    attributes: Attributes;
  };
}

export class PSLCard extends Component<IPSLCardProps> {
  render(): React.ReactNode {
    const sectorColors = [
      "#FFD3C0",
      "#ECD4FA",
      "#FFAEDF",
      "#c3e9b2",
      "#9bcbf5",
      "#ffa7b4",
    ];
    const {
      row: { attributes },
    } = this.props;

    const tooltipDescriptions: { [key: string]: string } = {
      hh_score:
        "Designed to provide an objective view of agency quality and performance. Calculated by proprietary algorithm that weights the following activity metrics: interview rate; placement volume; number of reviews.",
      interview_rate:
        "Percentage of candidates submitted by the recruitment agency that are selected for interview",
      placement_conversion_rate:
        "Percentage of roles filled versus roles worked.",
      average_fee_earnings: "Average placement fee secured on role filled.",
      total_fees: "Total amount of fees earned through placements.",
      avg__fee_rate: "Average placement fee (%) for all roles filled.",
    };
    return (
      <ErrorBoundary>
        <PerformanceCard>
          <Section>
            <Title>Agency</Title>
            <AgencyCard>
              <AgencyDetails>
                <h5>{attributes.primaryUser.name}</h5>
                <AgencyNameLink
                  href={`/companies/${attributes.id}/profile#/?back_to=/employer/analytics#/psl-performance#`}
                  title="Job Profile"
                  target="_blank"
                >
                  {attributes.agencyName}
                </AgencyNameLink>
                <h3></h3>

                <ReactTooltip
                  type="dark"
                  aria-haspopup="true"
                  id="hh-score"
                  className="tooltip-max-width"
                />
                <h4
                  data-tip={tooltipDescriptions["hh_score"]}
                  data-for="hh-score"
                >
                  HH score:{" "}
                </h4>

                <h3>{attributes.hhScore}</h3>

                <h6>out of 10</h6>
              </AgencyDetails>
              <AgencyRightSection>
                <AgencyImageSection>
                  <AgencyImage
                    src={attributes.primaryUser.profilePicture}
                    alt={attributes.primaryUser.title}
                  />
                </AgencyImageSection>
                <ReactTooltip
                  type="dark"
                  aria-haspopup="true"
                  id="ratingToolTip"
                  className="tooltip-max-width"
                />
                {
                  <span
                    data-tip="The star rating is calculated by taking the average score from all employer and candidate reviews"
                    data-for="ratingToolTip"
                  >
                    <Rating
                      rating={attributes?.primaryUser?.feedbackRating || 0}
                    />
                  </span>
                }
              </AgencyRightSection>
            </AgencyCard>
            <AgencyBottomSection>
              <div> Added to PSL on</div>{" "}
              <h4>
                {moment(attributes.primaryUser.activeOnPlatformSince).format(
                  "D MMM, YYYY"
                )}
              </h4>
            </AgencyBottomSection>
            <LineBreak />
          </Section>

          {attributes.sectors && (
            <Section>
              <Title>Sector Engagement</Title>
              <SectorSection hasSectors={attributes.sectors.length > 0}>
                {attributes.sectors.length > 0
                  ? attributes.sectors.map(({ name, count }, index) => (
                      <Sector key={name} color={sectorColors[index]}>
                        {name} <span>{count}</span>
                      </Sector>
                    ))
                  : "No Data Available"}
              </SectorSection>
            </Section>
          )}

          {attributes.submissions && (
            <Section>
              <Title>Submissions</Title>
              <CandidateInfoSection>
                {attributes.submissions.map(([label, value]) => (
                  <CandidateInfo key={label}>
                    <ReactTooltip
                      type="dark"
                      aria-haspopup="true"
                      id={label.toLowerCase().replace(/ /g, "_")}
                      className="tooltip-max-width"
                    />
                    {label === "Interview rate" ? (
                      <span
                        data-tip={tooltipDescriptions["interview_rate"]}
                        data-for={label.toLowerCase().replace(/ /g, "_")}
                      >
                        {label}
                      </span>
                    ) : (
                      <span>{label}</span>
                    )}

                    <span className="value">{value === null ? 0 : value}</span>
                  </CandidateInfo>
                ))}
              </CandidateInfoSection>
            </Section>
          )}
          {attributes.jobs && (
            <Section>
              <Title>Jobs</Title>
              <JobInfoSection>
                {attributes.jobs.map(([label, value]) => (
                  <JobsInfo key={label}>
                    <ReactTooltip
                      type="dark"
                      aria-haspopup="true"
                      id={label.toLowerCase().replace(/ /g, "_")}
                      className="tooltip-max-width"
                    />
                    {label === "Placement conversion rate" ? (
                      <span
                        data-tip={
                          tooltipDescriptions["placement_conversion_rate"]
                        }
                        data-for={label.toLowerCase().replace(/ /g, "_")}
                      >
                        {label}
                      </span>
                    ) : (
                      <span>{label}</span>
                    )}
                    <span className="value">{value === null ? 0 : value}</span>
                  </JobsInfo>
                ))}
              </JobInfoSection>
            </Section>
          )}
          {attributes.fees && (
            <Section>
              <Title>Fees</Title>
              <FeesInfoSection>
                {attributes.fees.map(([label, value]) => (
                  <FeesInfo key={label}>
                    <ReactTooltip
                      type="dark"
                      aria-haspopup="true"
                      id={label.toLowerCase().replace(/[\s.()%]/g, "_")}
                      className="tooltip-max-width"
                    />

                    <span
                      data-tip={
                        tooltipDescriptions[
                          label.toLowerCase().replace(/[\s.()%]/g, "_")
                        ]
                      }
                      data-for={label.toLowerCase().replace(/[\s.()%]/g, "_")}
                    >
                      {label}
                    </span>
                    <span className="value">{value === null ? 0 : value}</span>
                  </FeesInfo>
                ))}
              </FeesInfoSection>
            </Section>
          )}
        </PerformanceCard>
      </ErrorBoundary>
    );
  }
}
export default PSLCard;
